import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import Card from 'components/card';
import {
  MdAdd,
  MdEdit,
  MdBlock,
  MdCheckCircle,
  MdDelete,
} from 'react-icons/md';
import headhrService from 'ApiService/Placement/headhrService';
import toast from 'react-hot-toast';
import MultiSelect from 'components/multipleselectoption/Index';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const ManageHRs = () => {
  const { state } = useContext(AuthContext);
  const [hrs, setHrs] = useState([]);
  const [blockedHRs, setBlockedHRs] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [blockedCount, setBlockedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedHR, setSelectedHR] = useState(null);
  const [branches, setBranches] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('active');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedHRForAction, setSelectedHRForAction] = useState(null);

  const selectedBranch = state?.selectedBranch;
  const userRole = state?.user?.role;
  const userHrRole = state?.user?.hrRole;
  const userAllPermission = state?.user?.allPermission;

  const isHeadHR = userRole === 'headhr' || userHrRole === 'headhr';
  const isSuperAdmin = userRole === 'superadmin';
  const isJRHRWithPermission =
    (userRole === 'jrhr' || userHrRole === 'jrhr') && userAllPermission;
  const canDelete = isHeadHR || isSuperAdmin;
  const canBlock = isHeadHR || isSuperAdmin || isJRHRWithPermission;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    contact: '',
    branches: [],
    hrRole: 'hr',
    allPermission: false,
  });

  const [branchOptions, setBranchOptions] = useState([]);
  const [branchLabels, setBranchLabels] = useState([]);

  useEffect(() => {
    fetchBranches();
    fetchCounts();
  }, [selectedBranch]);

  useEffect(() => {
    fetchHRs();
  }, [activeTab, currentPage]);

  const fetchCounts = async () => {
    try {
      const [activeRes, blockedRes] = await Promise.all([
        headhrService.getAllHRs(selectedBranch, 1, 1, 'active'),
        headhrService.getAllHRs(selectedBranch, 1, 1, 'blocked'),
      ]);
      setActiveCount(activeRes.data.pagination?.totalRecords || 0);
      setBlockedCount(blockedRes.data.pagination?.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const fetchHRs = async () => {
    try {
      setLoading(true);
      const filterStatus = activeTab === 'blocked' ? 'blocked' : 'active';
      const response = await headhrService.getAllHRs(
        selectedBranch,
        currentPage,
        10,
        filterStatus
      );

      if (response.data.success) {
        if (activeTab === 'blocked') {
          setBlockedHRs(response.data.data);
          setHrs([]);
        } else {
          setHrs(response.data.data);
          setBlockedHRs([]);
        }
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching HRs:', error);
      toast.error('Failed to fetch HRs');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await headhrService.getBranches();
      if (response.data.success) {
        const branchList = response.data.branches || [];
        setBranches(branchList);
        setBranchLabels(branchList.map((b) => b.name));
        setBranchOptions(
          branchList.map((b) => ({ label: b.name, value: b._id }))
        );
      } else {
        setBranches([]);
        setBranchLabels([]);
        setBranchOptions([]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
      setBranchLabels([]);
      setBranchOptions([]);
    }
  };

  const handleCreateHR = async (e) => {
    e.preventDefault();
    try {
      const response = await headhrService.createHR(formData);

      if (response.data.success) {
        toast.success('HR created successfully');
        setShowCreateModal(false);
        setFormData({
          name: '',
          email: '',
          password: '',
          contact: '',
          branches: [],
          hrRole: 'hr',
          allPermission: false,
        });
        fetchHRs();
        fetchCounts();
      }
    } catch (error) {
      console.error('Error creating HR:', error);
      toast.error(error.response?.data?.message || 'Failed to create HR');
    }
  };

  const handleUpdateHR = async (e) => {
    e.preventDefault();
    try {
      const response = await headhrService.updateHR({
        ...formData,
        _id: selectedHR._id,
      });

      if (response.data.success) {
        toast.success('HR updated successfully');
        setShowEditModal(false);
        setSelectedHR(null);
        fetchHRs();
      }
    } catch (error) {
      console.error('Error updating HR:', error);
      toast.error(error.response?.data?.message || 'Failed to update HR');
    }
  };

  const confirmToggleBlock = (hr) => {
    setSelectedHRForAction(hr);
    setModalAction('block');
    setIsModalOpen(true);
  };

  const confirmDelete = (hr) => {
    setSelectedHRForAction(hr);
    setModalAction('delete');
    setIsModalOpen(true);
  };

  const handleToggleBlock = async () => {
    if (!selectedHRForAction) return;

    try {
      const response = await headhrService.blockUnblockHR(
        selectedHRForAction._id,
        !selectedHRForAction.isBlocked
      );

      if (response.data.success) {
        toast.success(
          selectedHRForAction.isBlocked
            ? 'HR unblocked successfully'
            : 'HR blocked successfully'
        );
        fetchHRs();
        fetchCounts();
      }
    } catch (error) {
      console.error('Error toggling block status:', error);
      toast.error(
        error.response?.data?.message || 'Failed to update HR status'
      );
    } finally {
      setIsModalOpen(false);
      setSelectedHRForAction(null);
      setModalAction(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedHRForAction) return;

    try {
      const response = await headhrService.deleteHR(selectedHRForAction._id);

      if (response.data.success) {
        toast.success('HR deleted successfully');
        fetchHRs();
        fetchCounts();
      }
    } catch (error) {
      console.error('Error deleting HR:', error);
      toast.error(error.response?.data?.message || 'Failed to delete HR');
    } finally {
      setIsModalOpen(false);
      setSelectedHRForAction(null);
      setModalAction(null);
    }
  };

  const openEditModal = (hr) => {
    setSelectedHR(hr);
    setFormData({
      name: hr.name,
      email: hr.email,
      password: '',
      contact: hr.contact,
      branches:
        hr.branches?.map((b) => ({ label: b.name, value: b._id })) || [],
      hrRole: hr.hrRole || 'hr',
      allPermission: hr.allPermission || false,
    });
    setShowEditModal(true);
  };

  const renderHRTable = (hrList, isBlockedTab = false) => {
    return (
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="pb-3 text-left text-sm font-medium text-gray-600">
              Name
            </th>
            <th className="pb-3 text-left text-sm font-medium text-gray-600">
              Email
            </th>
            <th className="pb-3 text-left text-sm font-medium text-gray-600">
              Contact
            </th>
            <th className="pb-3 text-left text-sm font-medium text-gray-600">
              Branches
            </th>
            <th className="pb-3 text-left text-sm font-medium text-gray-600">
              Role
            </th>
            <th className="pb-3 text-left text-sm font-medium text-gray-600">
              Permission
            </th>
            {!isBlockedTab && (
              <th className="pb-3 text-left text-sm font-medium text-gray-600">
                Status
              </th>
            )}
            <th className="pb-3 text-left text-sm font-medium text-gray-600">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan={isBlockedTab ? '7' : '8'}
                className="py-4 text-center text-gray-600"
              >
                Loading...
              </td>
            </tr>
          ) : hrList.length === 0 ? (
            <tr>
              <td
                colSpan={isBlockedTab ? '7' : '8'}
                className="py-4 text-center text-gray-600"
              >
                No HRs found
              </td>
            </tr>
          ) : (
            hrList.map((hr) => (
              <tr key={hr._id} className="border-b border-gray-100">
                <td className="py-3 text-sm font-medium text-navy-700 dark:text-white">
                  {hr.name}
                </td>
                <td className="py-3 text-sm text-gray-600">{hr.email}</td>
                <td className="py-3 text-sm text-gray-600">{hr.contact}</td>
                <td className="py-3 text-sm text-gray-600">
                  {hr.branches?.map((b) => b.name).join(', ') || 'All Branches'}
                </td>
                <td className="py-3">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      hr.hrRole === 'headhr'
                        ? 'bg-yellow-100 text-yellow-700'
                        : hr.hrRole === 'jrhr'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {hr.hrRole === 'headhr'
                      ? 'Head HR'
                      : hr.hrRole === 'jrhr'
                      ? 'JR HR'
                      : 'HR'}
                  </span>
                </td>
                <td className="py-3">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      hr.allPermission
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {hr.allPermission ? 'Full Permission' : 'Limited'}
                  </span>
                </td>
                {!isBlockedTab && (
                  <td className="py-3">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-medium ${
                        hr.isBlocked
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {hr.isBlocked ? 'Blocked' : 'Active'}
                    </span>
                  </td>
                )}
                <td className="py-3">
                  <div className="flex gap-2">
                    {!isBlockedTab && hr.hrRole !== 'headhr' && (
                      <button
                        onClick={() => openEditModal(hr)}
                        className="rounded-lg bg-blue-100 p-2 text-blue-600 transition-all hover:bg-blue-200"
                        title="Edit"
                      >
                        <MdEdit className="h-4 w-4" />
                      </button>
                    )}
                    {canBlock && (
                      <button
                        onClick={() => confirmToggleBlock(hr)}
                        className={`rounded-lg p-2 transition-all ${
                          hr.isBlocked
                            ? 'bg-green-100 text-green-600 hover:bg-green-200'
                            : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                        }`}
                        title={hr.isBlocked ? 'Unblock' : 'Block'}
                      >
                        {hr.isBlocked ? (
                          <MdCheckCircle className="h-4 w-4" />
                        ) : (
                          <MdBlock className="h-4 w-4" />
                        )}
                      </button>
                    )}
                    {canDelete && !isBlockedTab && hr.hrRole !== 'headhr' && (
                      <button
                        onClick={() => confirmDelete(hr)}
                        className="rounded-lg bg-red-100 p-2 text-red-600 transition-all hover:bg-red-200"
                        title="Delete"
                      >
                        <MdDelete className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  };

  return (
    <div className="mt-3">
      <Card extra="w-full pb-10 p-4">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
              Manage HRs
            </h4>
            <p className="text-sm text-gray-600">
              Create and manage HR team members
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-brand-600"
          >
            <MdAdd className="h-5 w-5" />
            Create HR
          </button>
        </div>

        <div className="mb-4 flex border-b border-gray-200">
          <button
            onClick={() => {
              setActiveTab('active');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'active'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Active HRs ({activeCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('blocked');
              setCurrentPage(1);
            }}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'blocked'
                ? 'border-b-2 border-brand-500 text-brand-500'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Blocked HRs ({blockedCount})
          </button>
        </div>

        <div className="overflow-x-auto">
          {activeTab === 'active'
            ? renderHRTable(hrs, false)
            : renderHRTable(blockedHRs, true)}
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="flex items-center px-4 text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </Card>

      {showCreateModal && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 dark:bg-navy-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                Create New HR
              </h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateHR} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  Password
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  Contact
                </label>
                <input
                  type="tel"
                  required
                  pattern="[0-9]{10}"
                  value={formData.contact}
                  onChange={(e) =>
                    setFormData({ ...formData, contact: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  HR Role <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.hrRole}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setFormData({
                      ...formData,
                      hrRole: newRole,
                      allPermission:
                        newRole === 'jrhr' ? formData.allPermission : false,
                      branches:
                        newRole === 'jrhr' && formData.allPermission
                          ? []
                          : formData.branches,
                    });
                  }}
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                >
                  <option value="hr">HR (Regular)</option>
                  <option value="jrhr">JR HR</option>
                </select>
              </div>

              {formData.hrRole === 'jrhr' && (
                <div className="flex items-center justify-between rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700">
                  <div>
                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                      All Permissions (Full Access)
                    </p>
                    <p className="text-xs text-gray-500">
                      Enable to give full access like Head HR
                    </p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={formData.allPermission}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allPermission: e.target.checked,
                          branches: e.target.checked ? [] : formData.branches,
                        })
                      }
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none dark:bg-navy-600 dark:after:border-navy-400"></div>
                  </label>
                </div>
              )}

              {!(formData.hrRole === 'jrhr' && formData.allPermission) && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-600">
                    Branches <span className="text-red-500">*</span>
                  </label>
                  <MultiSelect
                    options={branchLabels}
                    selected={formData.branches.map((b) => b.label)}
                    onChange={(selectedLabels) => {
                      const selected = branchOptions.filter((b) =>
                        selectedLabels.includes(b.label)
                      );
                      setFormData({ ...formData, branches: selected });
                    }}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-lg bg-gray-100 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-lg bg-brand-500 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
                >
                  Create HR
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedHRForAction(null);
          setModalAction(null);
        }}
        onConfirm={() => {
          if (modalAction === 'block') {
            handleToggleBlock();
          } else if (modalAction === 'delete') {
            handleDelete();
          }
        }}
        title={modalAction === 'delete' ? 'Delete HR' : 'Confirm Action'}
        message={
          modalAction === 'delete'
            ? `Are you sure you want to delete ${selectedHRForAction?.name}? This action cannot be undone.`
            : `Are you sure you want to ${
                selectedHRForAction?.isBlocked ? 'unblock' : 'block'
              } ${selectedHRForAction?.name}?`
        }
        confirmText={modalAction === 'delete' ? 'Delete' : 'Yes'}
        cancelText="Cancel"
        icon={modalAction === 'delete' ? 'danger' : 'warning'}
      />

      {showEditModal && selectedHR && (
        <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 dark:bg-navy-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                Edit HR
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdateHR} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  Contact
                </label>
                <input
                  type="tel"
                  required
                  pattern="[0-9]{10}"
                  value={formData.contact}
                  onChange={(e) =>
                    setFormData({ ...formData, contact: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  New Password (Optional)
                </label>
                <input
                  type="password"
                  minLength={8}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Leave blank to keep current password"
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-600">
                  HR Role <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={formData.hrRole}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setFormData({
                      ...formData,
                      hrRole: newRole,
                      allPermission:
                        newRole === 'jrhr' ? formData.allPermission : false,
                      branches:
                        newRole === 'jrhr' && formData.allPermission
                          ? []
                          : formData.branches,
                    });
                  }}
                  className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                >
                  <option value="hr">HR (Regular)</option>
                  <option value="jrhr">JR HR</option>
                </select>
              </div>

              {formData.hrRole === 'jrhr' && (
                <div className="flex items-center justify-between rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700">
                  <div>
                    <p className="text-sm font-medium text-navy-700 dark:text-white">
                      All Permissions (Full Access)
                    </p>
                    <p className="text-xs text-gray-500">
                      Enable to give full access like Head HR
                    </p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={formData.allPermission}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allPermission: e.target.checked,
                          branches: e.target.checked ? [] : formData.branches,
                        })
                      }
                      className="peer sr-only"
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-500 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none dark:bg-navy-600 dark:after:border-navy-400"></div>
                  </label>
                </div>
              )}

              {!(formData.hrRole === 'jrhr' && formData.allPermission) && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-600">
                    Branches <span className="text-red-500">*</span>
                  </label>
                  <MultiSelect
                    options={branchLabels}
                    selected={formData.branches.map((b) => b.label)}
                    onChange={(selectedLabels) => {
                      const selected = branchOptions.filter((b) =>
                        selectedLabels.includes(b.label)
                      );
                      setFormData({ ...formData, branches: selected });
                    }}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 rounded-lg bg-gray-100 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-lg bg-brand-500 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
                >
                  Update HR
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageHRs;
